import { useState } from 'react';
import { ModalControlModalProps, TextField, useControlledField } from '@frontend/design-system';
import { ActionModal, ActionModalContent } from '../shared/action-modal';
import { TrainPicker } from '../shared/train-picker';

interface CreateChannelModalProps {
  modalProps: ModalControlModalProps;
  existingChannelNames: string[];
  isSaving?: boolean;
  onConfirm: (channelName: string, trainId: string) => void;
}

export const CreateChannelModal = ({ modalProps, isSaving, ...contentProps }: CreateChannelModalProps) => (
  <ActionModal modalProps={modalProps} isSaving={isSaving}>
    <CreateChannelModalContent {...contentProps} isSaving={isSaving} onClose={modalProps.onClose} />
  </ActionModal>
);

type CreateChannelModalContentProps = Omit<CreateChannelModalProps, 'modalProps'> & { onClose: () => void };

const CreateChannelModalContent = ({
  existingChannelNames,
  isSaving,
  onClose,
  onConfirm,
}: CreateChannelModalContentProps) => {
  const [channelName, setChannelName] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const channelNameFieldProps = useControlledField({ type: 'text', value: channelName, onChange: setChannelName });
  const normalizedChannelName = channelName.trim().toLowerCase();
  const isDuplicateName = existingChannelNames.some((name) => name.toLowerCase() === normalizedChannelName);
  const canCreate = normalizedChannelName !== '' && !isDuplicateName && selectedId !== '';

  return (
    <ActionModalContent
      title='Create channel'
      onClose={onClose}
      isSaving={isSaving}
      confirmLabel='Create'
      savingLabel='Creating...'
      confirmDisabled={!canCreate}
      onConfirm={() => onConfirm(normalizedChannelName, selectedId)}
    >
      <TextField
        {...channelNameFieldProps}
        name='channel-name'
        label='Channel name'
        disabled={isSaving}
        helperText={isDuplicateName ? 'A channel with this name already exists.' : undefined}
      />
      <TrainPicker
        label='Train'
        name='train-search'
        selectedId={selectedId}
        onSelect={setSelectedId}
        disabled={isSaving}
      />
    </ActionModalContent>
  );
};
